/**
 * Task commands — the client's write half for editable tasks (IR-126 §4.1, §7.2).
 *
 * A mutation is a COMMAND DOC created under
 * `projects/{pid}/command_queues/task_edit/commands`. The browser never writes a
 * task document: the apply layer is the single writer, and it flips the command
 * `requested → in_progress → done|failed` while writing the task, its revision
 * and (where relevant) the proposal in one transaction.
 *
 * This is the primitive the rest of phase 3 builds on — submission, the
 * optimistic update, and the subscription that resolves it. It deliberately does
 * **not** decide what a failure means to the user: §4.5 dispatch is 3.3, and the
 * pending-chain rules are 3.2.
 *
 * Modelled on `useAssessmentIntents`, and different in one way that matters:
 * that queue has an ADMISSION step (`accepted|rejected|blocked`) which the
 * caller waits on, because execution can run for an hour. A task command has no
 * admission — it either applied or it did not, in a second or two — so there is
 * one resolution, and it carries the machine-readable `error_code` §4.5 branches
 * on rather than a message anyone parses.
 *
 * **The optimistic value is applied before the write, and that is the whole
 * point** — latency is hidden. It is also the most dangerous thing here: an edit
 * that looks saved but was not is the worst failure this feature can produce
 * (§7.2). So the contract below is deliberately blunt about the three outcomes,
 * and in particular about the one that is neither success nor failure.
 */
import { addDoc, collection, doc, onSnapshot, serverTimestamp } from 'firebase/firestore'
import type { DocumentReference, Firestore } from 'firebase/firestore'
import { useFirestore } from 'vuefire'
import { getAuth } from 'firebase/auth'
import { logger } from '@/utils/logger'
import type { RejectionReason } from '@/models/rejectionReason'

/** The eight operations the apply layer executes (§3.1, MYAC-1266). */
export type TaskCommandKind =
  | 'task_create'
  | 'task_edit'
  | 'task_unapprove'
  | 'task_reapprove'
  | 'task_archive'
  | 'task_comment_add'
  | 'task_comment_edit'
  | 'task_comment_delete'

/**
 * §4.5's error classes. `unknown` is the tolerant-reader fallback: a newer
 * service may return a class this build predates, and an unrecognised code must
 * degrade to "something went wrong" rather than crash a dispatch table.
 */
export type TaskEditErrorCode =
  | 'stale'
  | 'policy'
  | 'unauthorized'
  | 'task_not_found'
  | 'already_created'
  | 'invalid_task_id'
  | 'unknown_op'
  | 'comment_not_found'
  | 'unknown'

const KNOWN_ERROR_CODES: readonly string[] = [
  'stale',
  'policy',
  'unauthorized',
  'task_not_found',
  'already_created',
  'invalid_task_id',
  'unknown_op',
  // MYAC-1266. Distinct from `task_not_found` because the remedies differ:
  // the task is still open and readable, and the right response is to
  // refresh the thread, not to close the dialog.
  'comment_not_found',
]

/** A field change: a dotted LEAF path and the new value (§4.4). */
export interface FieldChange {
  field: string
  after: unknown
}

/** Who is acting. `ai_generated` is false from a browser — the rules pin it. */
export interface EditActor {
  user_id: string
  ai_generated: false
}

/** What re-approval does with content edited since approval. Required — §5.4. */
export type ContentMode = 'keep' | 'reset'

/** Common to all five. Note what is NOT here: `base_seq` — see below. */
interface TaskCommandCommon {
  task_id: string
}

/**
 * Approve a proposal, or create a task by hand.
 *
 * `proposal_ref` present means approve; absent means manual. No `base_seq`:
 * there is no prior revision to be stale against (§4.3).
 */
export interface TaskCreatePayload extends TaskCommandCommon {
  kind: 'task_create'
  proposal_ref?: string
  content?: Record<string, unknown>
  /**
   * MYAC-1639 — the hidden task of an action-plan row nobody has added yet,
   * created by the first comment or edit on it. Born `parked` and `pending`, it
   * stays off the board until `task_reapprove` accepts it. Only beside a
   * remediation row's `proposal_ref`; the server refuses it anywhere else.
   */
  parked?: true
}

export interface TaskEditPayload extends TaskCommandCommon {
  kind: 'task_edit'
  base_seq: number
  changes: FieldChange[]
}

/** A lifecycle transition and nothing more — content is untouched (§5.4). */
export interface TaskUnapprovePayload extends TaskCommandCommon {
  kind: 'task_unapprove'
  base_seq: number
  /**
   * What the proposal's `review_decision` becomes (§4.3).
   *
   * Optional, and omitted means `pending` — the server defaults it, which is
   * what un-approve has always meant. Sending it explicitly is only needed for
   * the other case.
   *
   * `rejected` is not a second operation: rejecting an already-accepted task
   * *is* an un-approve, because the task exists and has to be sent back. The
   * two arrive at the same handler because an accepted task's path is its
   * Kanban copy (MYAC-999).
   */
  decision?: 'pending' | 'rejected'
  /**
   * Why it was rejected. Only meaningful alongside `decision: 'rejected'` —
   * the server **refuses** the command outright if it arrives without one,
   * rather than dropping it, so this must not be sent on a plain send-back.
   *
   * Capped at 300 server-side. The browser writes command documents straight
   * to Firestore, so that bound first fires when the apply layer parses the
   * queued document — which is why the dialog bounds its own textarea rather
   * than relying on it.
   */
  review_rejection_comment?: string | null
  /**
   * MYAC-1089 — the end of the rejection's period, written as a Firestore
   * Timestamp. Only with `decision: 'rejected'` (the server refuses it on
   * anything else), and omitted for a rejection with no end: never `null`.
   */
  review_rejected_until?: Date
  /**
   * MYAC-1639 — which of the four set-aside reasons was picked. Rejection only,
   * like the two fields above; `other` is refused without a comment.
   */
  review_rejection_reason?: RejectionReason
}

export interface TaskReapprovePayload extends TaskCommandCommon {
  kind: 'task_reapprove'
  base_seq: number
  /**
   * Required, with no default — deliberately. A default would make reverting a
   * human's work a silent outcome, and §5.4's whole point is that the choice
   * belongs to the person who knows why the task was sent back.
   */
  content_mode: ContentMode
}

/** Retire a task. Terminal — un-archiving is not an operation (§5.7). */
export interface TaskArchivePayload extends TaskCommandCommon {
  kind: 'task_archive'
  base_seq: number
}

/**
 * The only thing a client writes.
 *
 * **Five payloads, not one**, discriminated on `kind` — mirroring
 * `core_models_services`' `TaskCommandAny` field for field. A single flat
 * interface with everything optional makes illegal states representable: a
 * create carrying a `base_seq`, an edit with no `changes`, a re-approval with
 * no `content_mode`. Each would type-check here and be refused by the apply
 * layer at runtime, which is the slowest possible place to learn about it.
 *
 * `base_seq` sits on four of the five rather than on the shared base for the
 * same reason it does server-side: `task_create` has nothing to be stale
 * against, so the type says so rather than a comment saying so.
 */
/**
 * Post a comment on a task's thread (MYAC-1266).
 *
 * **No `base_seq`, deliberately.** Every other operation against an existing
 * task carries one because it changes a field somebody else may have changed
 * first. A comment changes no field — it appends a document of its own — so
 * there is nothing to be stale against, and the server's type says so.
 *
 * It is also what the feature needs: this thread exists so several people can
 * work one nonconformity at once, and a design where a colleague editing the
 * priority makes your half-written note fail to post would defeat it.
 *
 * The author is **not** in the payload. It is stamped server-side from `actor`,
 * which the rules pin to the authenticated uid — attribution is the whole point
 * of the feature, so it is never something the browser asserts.
 */
export interface TaskCommentAddPayload extends TaskCommandCommon {
  kind: 'task_comment_add'
  /** Capped at `COMMENT_MAX_LENGTH` server-side; the composer bounds it too. */
  body: string
}

/** Reword one's own comment. Author-only — the server refuses anyone else. */
export interface TaskCommentEditPayload extends TaskCommandCommon {
  kind: 'task_comment_edit'
  comment_id: string
  body: string
}

/**
 * Withdraw one's own comment.
 *
 * Soft server-side: the row and its place in the thread stay, the body is
 * cleared, and the trail keeps what was said. These threads sit on
 * nonconformities, where the rule is disposition and never removal.
 */
export interface TaskCommentDeletePayload extends TaskCommandCommon {
  kind: 'task_comment_delete'
  comment_id: string
}

export type TaskCommandPayload =
  | TaskCreatePayload
  | TaskEditPayload
  | TaskUnapprovePayload
  | TaskReapprovePayload
  | TaskArchivePayload
  | TaskCommentAddPayload
  | TaskCommentEditPayload
  | TaskCommentDeletePayload

/**
 * How a submission ended.
 *
 * Three states, not two, and the third is the one §7.2 exists for:
 *
 * - `done` — applied. `seq` is the revision it produced.
 * - `failed` — refused. `errorCode` is what 3.3 branches on.
 * - `unresolved` — **we do not know.** The timeout elapsed with the command
 *   still in flight. It may yet apply. Reporting this as `failed` would be a
 *   lie in the more dangerous direction: the caller would roll back a value the
 *   server is about to store.
 */
export interface TaskCommandOutcome {
  commandId: string
  status: 'done' | 'failed' | 'unresolved'
  errorCode?: TaskEditErrorCode
  /** The apply layer's message. For logs and support, never for branching. */
  error?: string
  seq?: number
  taskPath?: string
}

export interface OptimisticUpdate {
  /** Applied immediately, before the write. */
  apply: () => void
  /**
   * Called on `failed` only — never on `unresolved`. A command that may still
   * apply must not have its value pulled out from under it; §7.2 asks for a
   * pending state there instead, which is 3.2d's job.
   */
  rollback: () => void
}

/**
 * Marks an error thrown *before* the command reached Firestore.
 *
 * The two ways this function rejects are not the same fact, and a caller
 * sequencing commands has to tell them apart. A write that never happened means
 * nothing will ever apply — anything composed against it is already doomed. A
 * listener that died after the write means the command is queued and may well
 * apply; the outcome is merely unobservable from here.
 *
 * Collapsing both into a bare `throw` forces the caller to guess, and the two
 * guesses have opposite consequences (§5.1).
 */
const NOT_QUEUED = Symbol('taskCommandNotQueued')

/** True when the command was never written, so nothing will ever apply it. */
export function isNotQueued(error: unknown): boolean {
  return typeof error === 'object' && error !== null && NOT_QUEUED in error
}

function markNotQueued(error: unknown): unknown {
  if (typeof error === 'object' && error !== null) {
    Object.defineProperty(error, NOT_QUEUED, { value: true, enumerable: false })
  }
  return error
}

export interface SubmitOptions {
  optimistic?: OptimisticUpdate
  /**
   * Called once the command document exists, with its id.
   *
   * The only moment the id is known while the command is still in flight — it
   * is minted by `addDoc` and the promise does not resolve until the command
   * settles. 3.2c needs exactly that window to record a pointer it can
   * re-attach to after a reload.
   */
  onQueued?: (commandId: string) => void
  /** Give up waiting for a terminal status after this long. */
  timeoutMs?: number
  firestore?: Firestore
}

/**
 * How long a command may stay in flight before the client stops waiting.
 *
 * Sized against what the pipeline actually does: a command is one Eventarc
 * delivery plus one Firestore transaction over three documents. The dev
 * end-to-end run measured ~2.8s per command including trigger latency. Fifteen
 * seconds is therefore several times the observed worst case while staying
 * inside the window where a user still associates the outcome with their own
 * action.
 *
 * Deliberately far shorter than `useAssessmentIntents`' 90 minutes: that one
 * bounds a listener watching a job, where being generous costs a listener and
 * being tight costs a silent failure. Here the pending state is a value on
 * screen that claims to be saved, so being generous costs the user's trust in
 * what they are looking at.
 */
const COMMAND_TIMEOUT_MS = 15_000

const COMMANDS = (projectId: string) =>
  `projects/${projectId}/command_queues/task_edit/commands`

/** Narrow a stored code to the union, degrading anything unrecognised. */
function toErrorCode(raw: unknown): TaskEditErrorCode | undefined {
  if (raw === null || raw === undefined) return undefined
  const value = String(raw)
  if (KNOWN_ERROR_CODES.includes(value)) return value as TaskEditErrorCode

  // The ONLY place the wire value still exists. Degrading is right — an
  // unrecognised class must reach a "something went wrong" branch rather than
  // crash a dispatch table — but doing it silently loses the one fact worth
  // having: a deployed service is answering with a class this build predates,
  // which is a front-end that needs shipping, not a user error.
  //
  // `warn` rather than `error`: the client behaved correctly. It survives
  // production, where `info` and `debug` are no-ops.
  logger.warn(`task command: unrecognised error_code "${value}" - degrading to unknown`)
  return 'unknown'
}

/**
 * Write the command doc.
 *
 * `status: 'requested'` is pinned by the rules, and the apply layer's
 * compare-and-set from `requested` is the mutex that makes a duplicate Eventarc
 * delivery a no-op (§5.2) — so the client must never write anything else.
 *
 * `actor.ai_generated` is `false` and the rules enforce it. This is the clause
 * R2 attribution rests on: without it a browser could forge an AI-authored edit
 * onto an audited record.
 */
async function deposit(
  db: Firestore,
  projectId: string,
  payload: TaskCommandPayload,
  uid: string,
): Promise<DocumentReference> {
  const actor: EditActor = { user_id: uid, ai_generated: false }
  return addDoc(collection(db, COMMANDS(projectId)), {
    status: 'requested',
    payload: { ...payload, actor },
    requested_by: uid,
    created_at: serverTimestamp(),
  })
}

/**
 * Apply the optimistic value, submit the command, and resolve when it settles.
 *
 * Never rejects for a refusal — a refused command is a *result*, returned as
 * `failed` with its `errorCode`. It rejects only when the submission itself
 * could not happen (not signed in, the write failed, the listener errored
 * before any status arrived), because those leave nothing to report an outcome
 * against.
 */
export async function submitTaskCommand(
  projectId: string,
  payload: TaskCommandPayload,
  options: SubmitOptions = {},
): Promise<TaskCommandOutcome> {
  const db = options.firestore ?? useFirestore()
  const uid = getAuth().currentUser?.uid
  if (!uid) throw markNotQueued(new Error('not signed in'))

  // Before the write, deliberately: this is what hides the latency, and it is
  // the reason a failure has to reach the caller rather than being swallowed.
  //
  // GUARDED, because `apply()` can throw and the classification matters more
  // than the throw does. It writes into the resolved task, and a target that
  // is a getter-only property makes Vue's reactive proxy return `false` from
  // its set trap — a `TypeError` in strict mode, raised BEFORE anything is
  // queued. Unmarked, that reached the user as "it may still go through",
  // which is the one thing it definitely will not: no command exists to apply
  // (MYAC-1109, observed on dev).
  try {
    options.optimistic?.apply()
  } catch (e) {
    // Best-effort unwind. `apply()` is not atomic — a multi-field edit can
    // write two fields and throw on the third, leaving the user looking at a
    // change no command will ever confirm. Its own failure is swallowed on
    // purpose: the caller needs the ORIGINAL cause, and a secondary throw
    // here would replace a diagnosable `TypeError` with noise.
    try {
      options.optimistic?.rollback()
    } catch (rollbackError) {
      // Still swallowed — the caller needs the ORIGINAL cause — but logged
      // (review #528): a rollback that itself throws leaves the optimistic
      // value on screen, and a silent one leaves nothing to diagnose it by.
      logger.error('optimistic rollback failed after a rejected submit', rollbackError)
    }
    throw markNotQueued(e)
  }

  let ref: DocumentReference
  try {
    ref = await deposit(db, projectId, payload, uid)
  } catch (e) {
    // Nothing was queued, so nothing will ever resolve this. Undo the optimistic
    // value here rather than leaving the user looking at a change that exists
    // only on their screen.
    options.optimistic?.rollback()
    throw markNotQueued(e)
  }

  options.onQueued?.(ref.id)

  return watchCommand(ref, {
    timeoutMs: options.timeoutMs,
    label: payload.kind,
    onRollback: () => options.optimistic?.rollback(),
  })
}

/** A reference to a command document, for re-attaching to one already written. */
export function commandRef(
  projectId: string,
  commandId: string,
  firestore?: Firestore,
): DocumentReference {
  const db = firestore ?? useFirestore()
  return doc(db, `${COMMANDS(projectId)}/${commandId}`)
}

export interface WatchOptions {
  timeoutMs?: number
  /** For log lines. The command kind when submitting, `recovered` on reload. */
  label?: string
  /**
   * Undo the optimistic value on a refusal.
   *
   * Optional because a **recovered** command has none: the reload took the
   * optimistic value with it, and the task snapshot is authoritative. There is
   * nothing to roll back, only something to report.
   */
  onRollback?: () => void
}

/**
 * Wait for a command document to reach a terminal status.
 *
 * Split out of {@link submitTaskCommand} so that reload recovery (3.2c) waits
 * on a re-attached command through **this exact code** rather than a second
 * copy of it. A copy would drift — and the parts most likely to drift are the
 * ones that took the longest to get right: that a missing document is not a
 * failure, that a timeout is `unresolved` rather than `failed`, and that a
 * listener error rejects without rolling back.
 */
export function watchCommand(
  ref: DocumentReference,
  options: WatchOptions = {},
): Promise<TaskCommandOutcome> {
  const label = options.label ?? 'task_command'

  return new Promise<TaskCommandOutcome>((resolve, reject) => {
    let settled = false
    let unsubscribe = () => {}

    const finish = (outcome: TaskCommandOutcome) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      unsubscribe()
      if (outcome.status === 'failed') options.onRollback?.()
      resolve(outcome)
    }

    const timer = setTimeout(() => {
      // Explicitly NOT a failure, and explicitly no rollback: the command may
      // still be in flight and may still apply (§7.2.1). The caller renders
      // "still processing"; 3.2d owns what that looks like.
      logger.info(`task command ${label} ${ref.id}: no terminal status in time`)
      finish({ commandId: ref.id, status: 'unresolved' })
    }, options.timeoutMs ?? COMMAND_TIMEOUT_MS)

    unsubscribe = onSnapshot(
      ref,
      (snap) => {
        const d = snap.data()
        // A missing document is not a failure (§7.2.3): pruning or a listener
        // attached before the write propagates both look like this. Keep
        // waiting — the timeout is what bounds it.
        if (!d) return

        const status = String(d.status ?? '')
        if (status !== 'done' && status !== 'failed') return

        const result = (d.payload as Record<string, unknown> | undefined)?.result as
          | Record<string, unknown>
          | undefined

        finish({
          commandId: ref.id,
          status,
          errorCode: toErrorCode(result?.error_code),
          error: (d.error as string) || undefined,
          seq: typeof result?.seq === 'number' ? (result.seq as number) : undefined,
          taskPath: (result?.task_path as string) || undefined,
        })
      },
      (e) => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        unsubscribe()
        // Rejects rather than resolving `unresolved`, and does NOT roll back.
        // The distinction: `unresolved` is a normal outcome the UI renders as
        // "still processing", whereas losing the listener is a fault the caller
        // must handle. But the command was written and may well apply, so
        // rolling back would contradict a server about to store the value —
        // same reasoning about the data, a different signal to the caller.
        logger.error(`task command ${label} ${ref.id}: listener failed`, e)
        reject(e)
      },
    )
  })
}

/**
 * Set aside one of a Product's proposed tasks that has no board task yet
 * (MYAC-1660): the `review_td_task` command the chat has sent since MYAC-1472.
 * The screen used to write the decision onto the proposal itself, so the two
 * writers could overwrite each other; now both go through assessments'
 * `TDCurrentService.review_task`, which refuses a second decision instead.
 *
 * It rides the `recount_proposals` queue, not `task_edit`: the proposal is not
 * a task, and the executor recounts the section once the decision is written.
 * The payload is strict server-side (`ReviewTDTaskCommand`), so it carries no
 * `actor` and nothing else this function does not name.
 *
 * A refusal resolves `failed` with the refusal code as `error`
 * (`unknown_task`, `already_decided:<decision>`, `owned_by_task`, `conflict`);
 * `errorCode` stays unset, since these are not the task ledger's classes.
 */
export async function submitTDTaskReview(
  projectId: string,
  taskId: string,
  comment: string | null,
  options: { timeoutMs?: number; firestore?: Firestore } = {},
): Promise<TaskCommandOutcome> {
  const db = options.firestore ?? useFirestore()
  const uid = getAuth().currentUser?.uid
  if (!uid) throw new Error('not signed in')

  const ref = await addDoc(
    collection(db, `projects/${projectId}/command_queues/recount_proposals/commands`),
    {
      status: 'requested',
      requested_by: uid,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp(),
      payload: { kind: 'review_td_task', task_id: taskId, comment },
    },
  )
  return watchCommand(ref, {
    // Longer than a task command's 15 s: this queue is served by assessments,
    // whose cold Eventarc handler takes ~25 s (MYAC-1439).
    timeoutMs: options.timeoutMs ?? 30_000,
    label: 'review_td_task',
  })
}
